/**
 * Audience Management Types
 * Story: E007-S027 Audience Manager & Targeting Optimization
 */

// Platform types
export type Platform = 'meta' | 'google' | 'tiktok' | 'all';

// Audience types
export type AudienceType = 'custom' | 'lookalike' | 'uploaded_list' | 'retargeting';

// Audience status
export type AudienceStatus = 'creating' | 'active' | 'syncing' | 'error' | 'archived';

// Demographics criteria
export interface DemographicsCriteria {
  age_min?: number;
  age_max?: number;
  genders?: ('male' | 'female' | 'all')[];
  locations?: string[];
}

// Full audience criteria
export interface AudienceCriteria {
  demographics?: DemographicsCriteria;
  interests?: string[];
  behaviors?: string[];
}

// Performance metrics
export interface AudiencePerformanceMetrics {
  impressions: number;
  clicks: number;
  conversions: number;
  spend: number;
  revenue: number;
  roas: number;
  ctr: number;
}

// Daily performance data point
export interface DailyPerformance {
  date: string;
  impressions: number;
  clicks: number;
  conversions: number;
  spend: number;
  revenue: number;
  roas: number;
  ctr: number;
}

// Campaign using audience
export interface CampaignUsing {
  campaign_id: string;
  campaign_name: string;
  status: string;
  platform: Platform;
  impressions: number;
  spend: number;
  roas: number;
}

// Audience segment
export interface Audience {
  id: string;
  name: string;
  description?: string;
  platform: Platform;
  platform_audience_id?: string;
  type: AudienceType;
  criteria: AudienceCriteria;
  size?: number;
  status: AudienceStatus;
  parent_audience_id?: string;
  parent_audience_name?: string;
  similarity_percent?: number;
  performance?: AudiencePerformanceMetrics;
  campaign_count?: number;
  last_used_at?: string;
  last_synced_at?: string;
  created_at?: string;
  updated_at?: string;
}

// Recommendation
export interface Recommendation {
  type: string;
  priority: 'high' | 'medium' | 'low';
  message: string;
  action: string;
}

// Detailed performance response
export interface AudiencePerformanceDetail {
  audience: Audience;
  metrics: {
    daily: DailyPerformance[];
    totals: AudiencePerformanceMetrics;
    campaigns_using: CampaignUsing[];
  };
  recommendations: Recommendation[];
}

// Pagination metadata
export interface PaginationMetadata {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// List response
export interface AudienceListResponse {
  audiences: Audience[];
  metadata: PaginationMetadata;
}

// Create audience request
export interface CreateAudienceRequest {
  name: string;
  description?: string;
  platform: Platform;
  type?: AudienceType;
  criteria?: AudienceCriteria;
}

// Create audience response
export interface CreateAudienceResponse {
  id: string;
  platform_audience_id?: string;
  estimated_size: number;
  status: AudienceStatus;
}

// Create lookalike request
export interface CreateLookalikeRequest {
  name: string;
  similarity_percent: number;
  platform: Platform;
  location?: string;
}

// Create lookalike response
export interface CreateLookalikeResponse {
  lookalike_audience_id: string;
  estimated_size: number;
  status: AudienceStatus;
}

// Update audience request
export interface UpdateAudienceRequest {
  name?: string;
  description?: string;
  criteria?: AudienceCriteria;
  status?: 'active' | 'archived';
}

// Sync to platform request
export interface SyncToPlatformRequest {
  target_platform: Platform;
}

// Sync response
export interface SyncResponse {
  new_audience_id: string;
  target_platform: Platform;
  status: AudienceStatus;
}

// Platform info
export interface PlatformInfo {
  id: Platform;
  name: string;
  features: string[];
}

// Interest suggestion
export interface InterestSuggestion {
  suggestions: string[];
  platform: Platform;
}

// Location suggestion
export interface LocationSuggestion {
  code: string;
  name: string;
  type: 'country' | 'region' | 'city';
}

// Size estimate response
export interface SizeEstimateResponse {
  estimated_size: number;
  platform: Platform;
  criteria: AudienceCriteria;
}

// Filter options for audience list
export interface AudienceFilters {
  search?: string;
  platform?: Platform;
  type?: AudienceType;
  status?: AudienceStatus;
  sort?: string;
  page?: number;
  limit?: number;
}

// Sort options
export const AUDIENCE_SORT_OPTIONS = [
  { value: 'created_at_desc', label: 'Newest First' },
  { value: 'created_at_asc', label: 'Oldest First' },
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'size_desc', label: 'Size (High to Low)' },
  { value: 'size_asc', label: 'Size (Low to High)' },
  { value: 'roas_desc', label: 'ROAS (High to Low)' },
  { value: 'roas_asc', label: 'ROAS (Low to High)' },
  { value: 'last_used_desc', label: 'Recently Used' },
] as const;

// Platform display info
export const PLATFORM_INFO: Record<Platform, { name: string; color: string; icon: string }> = {
  meta: { name: 'Meta', color: 'blue', icon: 'facebook' },
  google: { name: 'Google', color: 'red', icon: 'google' },
  tiktok: { name: 'TikTok', color: 'pink', icon: 'tiktok' },
  all: { name: 'All Platforms', color: 'gray', icon: 'globe' },
};

// Audience type display info
export const AUDIENCE_TYPE_INFO: Record<AudienceType, { name: string; description: string }> = {
  custom: { name: 'Custom', description: 'Created from targeting criteria' },
  lookalike: { name: 'Lookalike', description: 'Similar users to a seed audience' },
  uploaded_list: { name: 'Uploaded List', description: 'From customer data upload' },
  retargeting: { name: 'Retargeting', description: 'Website or app visitors' },
};

// Status display info
export const STATUS_INFO: Record<AudienceStatus, { name: string; color: string }> = {
  creating: { name: 'Creating...', color: 'yellow' },
  active: { name: 'Active', color: 'green' },
  syncing: { name: 'Syncing...', color: 'blue' },
  error: { name: 'Error', color: 'red' },
  archived: { name: 'Archived', color: 'gray' },
};

// Format audience size for display
export function formatAudienceSize(size: number | undefined): string {
  if (!size) return '-';
  if (size >= 1000000) {
    return `${(size / 1000000).toFixed(1)}M`;
  }
  if (size >= 1000) {
    return `${(size / 1000).toFixed(0)}K`;
  }
  return size.toString();
}

// Format ROAS for display
export function formatROAS(roas: number | undefined): string {
  if (!roas) return '-';
  return `${roas.toFixed(1)}x`;
}

// Format CTR for display
export function formatCTR(ctr: number | undefined): string {
  if (!ctr) return '-';
  return `${ctr.toFixed(2)}%`;
}

// Format currency
export function formatCurrency(value: number | undefined): string {
  if (value === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// Format date relative to now
export function formatRelativeDate(dateString: string | undefined): string {
  if (!dateString) return 'Never';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}
